import React from 'react';
import { Brain, Calculator, Play } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';

const PracticePage = ({ 
  testType, 
  setTestType, 
  difficulty, 
  setDifficulty, 
  startTest, 
  loading,
  setCurrentPage,
  goToLeaderboard,
  mobileMenuOpen,
  setMobileMenuOpen
}) => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-500 via-purple-600 to-purple-800 overflow-x-hidden">
      <Header 
        setCurrentPage={setCurrentPage} 
        goToLeaderboard={goToLeaderboard}
        mobileMenuOpen={mobileMenuOpen}
        setMobileMenuOpen={setMobileMenuOpen}
      />

      <div className="pt-20 p-4">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-8 sm:mb-12 text-white px-2">
            <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-4">Choose Your Training</h1>
            <p className="text-base sm:text-lg md:text-xl opacity-90">Select test type and difficulty level to begin your preparation</p>
          </div>

          <div className="bg-white rounded-2xl shadow-2xl p-4 sm:p-6 md:p-8">
            <h2 className="text-xl sm:text-2xl font-bold text-gray-800 mb-6 sm:mb-8 text-center">Test Configuration</h2>
            
            {/* Test Type Selection */}
            <div className="mb-6 sm:mb-8">
              <label className="block text-gray-700 font-semibold mb-4 text-base sm:text-lg">Training Type</label>
              <div className="grid grid-cols-2 gap-4 sm:gap-6">
                <div 
                  onClick={() => setTestType('mental_math')}
                  className={`p-4 sm:p-6 rounded-xl border-2 cursor-pointer transition-all ${
                    testType === 'mental_math' 
                      ? 'border-blue-500 bg-blue-50' 
                      : 'border-gray-200 hover:border-blue-300'
                  }`}
                >
                  <Brain className="w-8 h-8 sm:w-10 sm:h-10 text-blue-600 mb-4" />
                  <h3 className="text-lg sm:text-xl font-semibold mb-2">Mental Math</h3>
                  <p className="text-sm sm:text-base text-gray-600">100 questions, 10 minutes</p>
                  <p className="text-xs sm:text-sm text-gray-500 mt-2">Fast calculations under pressure. Master the numbers that move markets.</p>
                </div>
                <div 
                  onClick={() => setTestType('sequences')}
                  className={`p-4 sm:p-6 rounded-xl border-2 cursor-pointer transition-all ${
                    testType === 'sequences' 
                      ? 'border-purple-500 bg-purple-50' 
                      : 'border-gray-200 hover:border-blue-300'
                  }`}
                >
                  <Calculator className="w-8 h-8 sm:w-10 sm:h-10 text-purple-600 mb-4" />
                  <h3 className="text-lg sm:text-xl font-semibold mb-2">Sequences</h3>
                  <p className="text-sm sm:text-base text-gray-600">25 sequences, 25 minutes</p>
                  <p className="text-xs sm:text-sm text-gray-500 mt-2">Train your pattern instinct. Develop the recognition skills that reveal hidden market signals.</p>
                </div>
              </div>
            </div>
            
            {/* Difficulty Selection */}
            <div className="mb-6 sm:mb-8">
              <label className="block text-gray-700 font-semibold mb-4 text-base sm:text-lg">
                Difficulty Level
              </label>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
                {[
                  { level: 'easy', color: 'green', desc: 'Great for beginners' },
                  { level: 'medium', color: 'blue', desc: 'Standard interview level' },
                  { level: 'hard', color: 'red', desc: 'Top-tier firm level' }
                ].map(({ level, color, desc }) => {
                  const activeClasses = {
                    green: 'border-green-500 bg-green-50 text-green-700',
                    blue: 'border-blue-500 bg-blue-50 text-blue-700',
                    red: 'border-red-500 bg-red-50 text-red-700'
                  };

                  return (
                    <button
                      key={level}
                      onClick={() => setDifficulty(level)}
                      className={`p-3 sm:p-4 rounded-xl border-2 text-center transition-all capitalize ${
                        difficulty === level
                          ? activeClasses[color]
                          : 'border-gray-200 text-gray-700 hover:border-gray-300'
                      }`}
                    >
                      <div className="font-semibold text-base sm:text-lg">{level}</div>
                      <div className="text-xs sm:text-sm opacity-75 mt-1">{desc}</div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Test Preview */}
            <div className="bg-gray-50 rounded-xl p-4 sm:p-6 mb-6 sm:mb-8">
              <h3 className="font-semibold text-gray-800 mb-3 text-sm sm:text-base">Test Preview</h3>
              <div className="grid grid-cols-2 gap-3 sm:gap-4 text-center text-xs sm:text-sm">
                <div>
                  <div className="font-semibold text-gray-700">Type</div>
                  <div className="text-gray-600 capitalize">{testType.replace('_', ' ')}</div>
                </div>
                <div>
                  <div className="font-semibold text-gray-700">Questions</div>
                  <div className="text-gray-600">{testType === 'sequences' ? '25' : '100'}</div>
                </div>
                <div>
                  <div className="font-semibold text-gray-700">Time Limit</div>
                  <div className="text-gray-600">{testType === 'sequences' ? '25 min' : '10 min'}</div>
                </div>
                <div>
                  <div className="font-semibold text-gray-700">Difficulty</div>
                  <div className="text-gray-600 capitalize">{difficulty}</div>
                </div>
              </div>
            </div>
            
            {/* Start Button */}
            <div className="text-center">
              <button
                onClick={startTest}
                disabled={loading}
                className="w-full sm:w-auto px-8 sm:px-12 py-3 sm:py-4 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl font-bold text-base sm:text-xl hover:shadow-lg transform hover:-translate-y-0.5 transition-all disabled:opacity-50 flex items-center justify-center gap-3 mx-auto"
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 sm:h-6 sm:w-6 border-b-2 border-white"></div>
                    Loading Test...
                  </>
                ) : (
                  <>
                    <Play className="w-5 h-5 sm:w-6 sm:h-6" />
                    Start Test
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
      <div className="h-32"></div>
      <Footer setCurrentPage={setCurrentPage} goToLeaderboard={goToLeaderboard} />
    </div>
  );
};

export default PracticePage;